// U5 holding reply: `b` on a card that needs a reply asks the server for "Got this, I'll get back to
// you by Friday" in the owner's voice. It lands on the card as an editable draft with its own send,
// which is the highlighted step: `s` arms it and a second press confirms, like any send. Pressing
// `b` again goes to the holding draft (the owner's text is never rewritten).
import { useCallback, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CardView } from "@shared/api";
import type { CardAction } from "@shared/core";
import { needsReply } from "@shared/drafts";
import {
  HOLDING_ACTION_ID,
  HOLDING_BLOCK_ID,
  namesHoldingDay,
  type HoldingReplyView,
} from "@shared/unstick";
import { isApiError } from "@web/lib/api";
import { acceptCard } from "@web/lib/feedQueries";
import { unstickApi, unstickKeys } from "@web/lib/unstickApi";
import { useCommand } from "@web/shell/commands";
import { isToReview } from "../selectors";

type Holdable = Pick<CardView, "kind" | "ask" | "status" | "threadKey"> & {
  actions: readonly CardAction[];
};

/** A card waiting for the owner that needs a reply, or already carries a reply to send. */
export function canHoldReply(card: Holdable): boolean {
  if (!isToReview(card) || !card.threadKey) return false;
  return (
    needsReply(card) ||
    card.actions.some((a) => a.behavior === "approve_action" && a.actionKind === "gmail.send_reply")
  );
}

/**
 * The calm note when the text and the picked day disagree (the owner typed another day, or took
 * it out); null when the text still names `phrase`. The send stays unarmed while there is one.
 */
export function holdingDayNote(text: string, phrase: string): string | null {
  if (namesHoldingDay(text, phrase)) return null;
  return `Your reply no longer says “${phrase}”. Pick the day it names above, or put “${phrase}” back, so the task matches.`;
}

/** Why the holding send can't arm yet (its text and the picked day disagree), or null. */
export function holdingArmBlock(
  actionId: string,
  holding: Pick<HoldingReplyView, "phrase" | "status"> | null | undefined,
  text: string | undefined,
): string | null {
  if (actionId !== HOLDING_ACTION_ID || !holding || holding.status !== "drafted") return null;
  return holdingDayNote(text ?? "", holding.phrase);
}

export const KEPT_TYPING_NOTE =
  "You kept typing, so your words stay as they are. Change the day in them to match.";

/**
 * What the editor shows once a day change comes back: the server's text only when the editor
 * still holds exactly what was sent; otherwise the owner's newer typing stays, with a note.
 */
export function afterDayChange(
  submitted: string,
  current: string,
  next: string | undefined,
): { text: string | null; note: string | null } {
  if (current !== submitted) return { text: null, note: KEPT_TYPING_NOTE };
  return { text: next ?? null, note: null };
}

export function holdingEditor(cardId: string): HTMLTextAreaElement | null {
  return document.querySelector<HTMLTextAreaElement>(
    `[data-card-editor="${CSS.escape(cardId)}"][data-block-id="${HOLDING_BLOCK_ID}"]`,
  );
}

function failure(error: unknown): string {
  if (isApiError(error, "conflict") && error.serverMessage) return `${error.serverMessage}.`;
  if (isApiError(error, "stale") || isApiError(error, "conflict"))
    return "This card just changed. Look again, then press B.";
  if (isApiError(error, "invalid_request") && error.serverMessage) return `${error.serverMessage}.`;
  return "Couldn't write a holding reply just now. Nothing changed.";
}

/** Writes the holding reply onto the card; resolves true when it is there. */
export async function writeHoldingReply(
  queryClient: QueryClient,
  cardId: string,
): Promise<boolean> {
  const pending = toast("Writing a holding reply…");
  try {
    const res = await unstickApi.draftHolding(cardId);
    acceptCard(queryClient, res.card);
    queryClient.setQueryData(unstickKeys.holding(cardId), { holding: res.holding });
    toast.dismiss(pending);
    toast(`Holding reply ready: you'll reply by ${res.holding.phrase}. Press S to send it.`);
    return true;
  } catch (error) {
    toast.dismiss(pending);
    toast(failure(error));
    return false;
  }
}

/** Binds `b` while the card is active; returns the same action for buttons. */
export function useHoldingReplyKey(card: CardView | undefined, on: boolean): () => Promise<void> {
  const queryClient = useQueryClient();
  const busy = useRef(false);
  const start = useCallback(async () => {
    if (!card || busy.current) return;
    const editor = holdingEditor(card.id);
    if (editor) {
      editor.focus();
      return;
    }
    if (!canHoldReply(card)) {
      toast("This card isn't waiting for a reply from you.");
      return;
    }
    busy.current = true;
    try {
      await writeHoldingReply(queryClient, card.id);
    } finally {
      busy.current = false;
    }
  }, [card, queryClient]);
  useCommand("card.holdingReply", () => void start(), on);
  return start;
}
